//---- REACT and JS 
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
//---- Ant Design
import { Col, Row, Card } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
//---- Project
import { useRequiredAccess } from "hooks/useRequiredAccess";
import { ORG_TYPES } from "components/Common/Constants";
import { setPageLoader } from "reducers/loaderSlice";
import { setVehicleDetails } from "reducers/eolSlice";
import { getVehicleDetails, getVehicleHealthStatus } from "apis/vehicleHealthApis";
import VehicleDetails from "components/Dashboard/VehicleDetails";
import LiveDateTime from "components/Common/LiveDateTime";
import VehicleCarousel from "./VehicleCarousel";
import VehicleHealthTable from "./VehicleHealthTable";
import Loading from "components/Common/Loading";
import APIError from "components/Common/APIError";

import "./style.css";
import { useLocation } from "react-router-dom";

const VehicleHealth = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const vehicleDetails = useSelector((state) => state.eol.vehicleDetails);
  const [vehicle, setVehicle] = useState(location?.state?.vehicleDetails);
  const [partsHealth, setPartsHealth] = useState([]);
  const [partsHovered, setPartsHovered] = useState({});

  const checkAccess = useRequiredAccess(
    [],
    [ORG_TYPES.ADMINISTRATOR, ORG_TYPES.MANUFACTURER]
  );

  const {
    data: vehicleData,
    error: vehicleError,
  } = useQuery({
    queryKey: ["vehicleDetails", vehicle?.vehId],
    queryFn: () => getVehicleDetails(vehicle?.vehId),
    keepPreviousData: true,
    enabled: !!vehicle?.vehId,
    staleTime: 1 * 10 * 60 * 1000,
  });

  const {
    data: vehicleHealthData,
    isLoading: vehicleHealthLoading,
    isError: vehicleHealthIsError,
    error: vehicleHealthError,
  } = useQuery({
    queryKey: ["vehicleHealth", vehicle?.vehId],
    queryFn: () => getVehicleHealthStatus(vehicle?.vehId),
    keepPreviousData: true,
    enabled: !!vehicle?.vehId,
    staleTime: 1 * 10 * 60 * 1000,
  });

  useEffect(() => {
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setVehicleDetails(vehicleData));
      // eslint-disable-next-line
  }, [vehicleData, dispatch]);

  useEffect(() => {
    if (vehicle?.vehImei && vehicle?.vehImei !== "") {
      dispatch(setPageLoader("refresh"));
      if (vehicleHealthData?.parts) {
        setPartsHealth(vehicleHealthData?.parts);
        let partInfo = {};
        const partStatus = vehicleHealthData?.parts?.reduce((acc, field) => {
          acc[field?.partType] = field?.status;
          partInfo[field?.partType] = field?.info;
          return acc;
        }, {});
        setPartsHovered({ status: partStatus, info: partInfo });
      } else {
        setPartsHealth([]);
      }
      dispatch(setPageLoader(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleHealthData, vehicle?.vehImei]);

  const onChangeImei = (_, value) => {
    setVehicle(value);
  };

  const handleMouseEnterRow = (rowindex, values) => {
    setPartsHovered((prev) => ({
      ...prev,
      [values?.partType]: true,
      activePart: values?.partType,
      activeInfo: prev?.info?.[values?.partType],
    }));
  };

  const handleMouseLeaveRow = () => {
    setPartsHovered((prev) => ({ status: prev.status, info: prev.info }));
  };

  return (
    <div data-testid="vehicle-health" className="vehicle-health-page">
      <Row gutter={[16, 16]} className="mt1">
        <Col xs={24} sm={14} md={12} lg={10} xl={7}>
          <div className="lable">Vehicle ID</div>
          <VehicleDetails
            onChangeImei={onChangeImei}
            imei={vehicle}
            card={false}
          />
        </Col>
        <Col xs={24} sm={10} md={12} lg={14} xl={17} className="justify-right">
          <LiveDateTime />
        </Col>
      </Row>
      {vehicleHealthLoading && <Loading />}
      {vehicleHealthIsError && (<APIError message={vehicleError.message} />)}
      {!vehicleHealthError && vehicleHealthData && (
      <Row gutter={[16, 16]} className="mt1 mb1">
        <Col xs={24} sm={24} md={14} lg={12} xl={12}>
          <Row gutter={[16, 16]} className="h100">
            <Col span={24} className="text-center vehicle-part-image-col">
              <VehicleCarousel
                profileImages={vehicleHealthData?.profileImages}
                partsHovered={partsHovered}
                setPartsHovered={setPartsHovered}
              />
            </Col>
            <Col span={24} className="text-center">
              <div className="vehicle-number-title w100">Vehicle Number</div>
              <div className="vehicle-number w100">
                {vehicleDetails?.regNo || "XX XX XX 0000"}
              </div>
            </Col>
            <Col span={12}>
              <Card className="align-center pd05">
                <div>Odometer</div>
                {vehicleDetails?.odometer ? (
                  <div className="bold mt05">{vehicleDetails?.odometer}</div>
                ) : (
                  <div className="disabled mt05">Not Available</div>
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card className="align-center pd05">
                <div>Sale Date</div>
                {vehicleDetails?.saleDate ? (
                  <div className="bold mt05">
                    {dayjs(vehicleDetails?.saleDate).format("Do MMMM YYYY")}
                  </div>
                ) : (
                  <div className="disabled mt05">Not Available</div>
                )}
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={24} md={10} lg={12} xl={12}>
          <Row className="white-bg border h100 p0">
            <VehicleHealthTable
              vehiclePartList={partsHealth}
              partsHovered={partsHovered}
              handleMouseEnterRow={handleMouseEnterRow}
              handleMouseLeaveRow={handleMouseLeaveRow}
            />
            <Col span={24} className="align-end ">
              {partsHovered?.activePart ? (
                <div className="summaries border w100">
                  {partsHovered?.activeInfo ? (
                    <>
                      <div span={24} className="title">
                        {partsHovered.activePart.slice(0, 1) +
                          partsHovered.activePart
                            .slice(1, partsHovered.activePart.length)
                            .toLowerCase()}
                        {partsHovered.activePart === "TYRE" ? " Pressure" : ""}
                      </div>
                      <div span={24} className="mt05 text-justify desc">
                        &nbsp; &nbsp; &nbsp;{partsHovered.activeInfo}
                      </div>
                    </>
                  ) : (
                    <div span={24} className="disabled head18 plr05">
                      <ExclamationCircleOutlined
                        className="mr025"
                        style={{ fontSize: "18px", color: "#bcbcbc", padding: "8px", }}
                      />
                      <span
                        style={{ fontSize: "16px", color: "#bcbcbc", }}
                      >
                        Part health is not available currently
                      </span>
                    </div>
                  )}
                </div>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default VehicleHealth;
